/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:no-non-null-assertion
// tslint:disable:no-magic-numbers
// tslint:disable:no-null-keyword

import {
  chartLegendCellBar,
  chartLegendCellLineWithDots,
  chartLegendRow,
  ChartSpecsWithLegend,
} from '@gh/builder-shared-charts';
import { GrossProfitAndOverheadsDto, GrossProfitAndOverheadsValue } from '@gh/builder-shared-data';
import { DateTimeTypes, NumberTypes } from '@gh/common-shared';
import { inDefaultCurrency, offsetDate } from '@gh/core-util';
import { fill, head, last } from 'lodash';

export type GrossProfitAndOverheads = GrossProfitAndOverheadsDto;
export const grossProfitAndOverheadsFromDto = (dto: GrossProfitAndOverheadsDto): ChartSpecsWithLegend => {
  let maxCols: number = 1;
  const dataTable: any[][] = [];
  const options: any = {
    chartArea: {
      width: '75%',
      height: '70%',
    },
    isStacked: true,
    seriesType: 'bars',
    bar: {
      groupWidth: '25%',
    },
    colors: [
      '#003342', '#003e4f', '#00485c', '#005269', '#005c76', '#006683', '#007090',
      '#177d9a', '#2e8aa4', '#4597ae', '#5ca4b8', '#73b1c2', '#8bbecc', '#a2cbd6',
      '#b9d8e0', '#d0e5ea', '#e7f2f4',
    ],
    series: {},
  };

  dto.values.forEach((value: GrossProfitAndOverheadsValue) => {
    const maxYValue = value.yAxisValue.values.length;
    if (maxYValue && maxCols < maxYValue) {
      maxCols = maxYValue;
    }
  });

  dataTable.push([
    { type: 'string', label: '' },
  ]);

  dto.values.forEach((value: GrossProfitAndOverheadsValue, typeIndex: number) => {
    for (let i = 0; i < maxCols; i++) {
      head(dataTable)!.push(...[
        { type: 'number', label: 'lbl_gross_profit' },
        { type: 'string', role: 'tooltip', p: { html: true } },
      ]);
    }
  });

  head(dataTable)!.push(...[
    { type: 'number', label: 'lbl_overheads' },
    { type: 'string', role: 'tooltip', p: { html: true } },
  ]);

  head(dataTable)!
    .filter((item) => item.label)
    .forEach((item: any, index: number) => {
      options.series[index] = { visibleInLegend: false };
    });

  const averageIndex = (head(dataTable)!.length - 1) / 2 - 1;
  options.series[0] = { visibleInLegend: true };
  options.series[averageIndex] = { type: 'line', color: '#00ca00', visibleInLegend: true };

  dto.values.forEach((value: GrossProfitAndOverheadsValue) => {
    const rowValues: any[][] = [];
    const date = DateTimeTypes.ShortMonthYear
      .format(offsetDate(new Date(value.xAxisValue)))
      .split(' ')
      .join('\n');

    rowValues.push([
      date,
      ...fill(Array(head(dataTable)!.length - 1), null),
    ]);

    value.yAxisValue.values
      .forEach((v: number, vIndex: number) => {
        for (let i = 0; i < maxCols; i++) {
          last(rowValues)![vIndex * 2 + 1] = v;

          const tooltipValue = value.yAxisValue.tooltip[vIndex];
          const tooltipPriceValue = inDefaultCurrency(NumberTypes.Price.format(tooltipValue.value));
          last(rowValues)![vIndex * 2 + 2] = {
            label: 'lbl_gross_profit_cnt',
            value: {
              count: `${tooltipValue.key}: ${tooltipPriceValue}`,
            },
          };
        }
      });

    const price = inDefaultCurrency(NumberTypes.Price.format(value.yAxisValue.overheadsValue));
    last(rowValues)![head(dataTable)!.length - 2] = value.yAxisValue.overheadsValue;
    last(rowValues)![head(dataTable)!.length - 1] = {
      label: 'lbl_overheads_cnt',
      value: {
        count: price,
      },
    };

    dataTable.push(...rowValues);
  });

  return {
    legendLayout: [
      chartLegendRow(
        chartLegendCellBar('lbl_gross_profit', '#003342'),
        chartLegendCellLineWithDots('lbl_overheads', '#00ca00')),
    ],
    chartType: 'ComboChart',
    dataTable,
    options,
  };
};
